import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const CompanyStatistics = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animatedStats, setAnimatedStats] = useState({});

  const statistics = [
    {
      id: 'years',
      icon: 'Calendar',
      value: 5,
      suffix: '',
      label: 'Years in Business',
      description: 'Established in 2020',
      color: 'primary'
    },
    {
      id: 'projects',
      icon: 'Briefcase',
      value: 500,
      suffix: '+',
      label: 'Successful Projects',
      description: 'Delivered worldwide',
      color: 'success'
    },
    {
      id: 'retention',
      icon: 'Heart',
      value: 98,
      suffix: '%',
      label: 'Client Retention Rate',
      description: 'Long-term partnerships',
      color: 'accent'
    },
    {
      id: 'team',
      icon: 'Users',
      value: 45,
      suffix: '+',
      label: 'Expert Team Members',
      description: 'AI specialists & consultants',
      color: 'warning'
    },
    {
      id: 'clients',
      icon: 'Building2',
      value: 50,
      suffix: '+',
      label: 'Enterprise Clients',
      description: 'Fortune 500 companies',
      color: 'secondary'
    },
    {
      id: 'roi',
      icon: 'TrendingUp',
      value: 340,
      suffix: '%',
      label: 'Average ROI',
      description: 'For client implementations',
      color: 'primary'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById('statistics-section');
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      statistics.forEach((stat) => {
        let start = 0;
        const end = stat.value;
        const duration = 2000;
        const increment = end / (duration / 16);

        const animate = () => {
          start += increment;
          if (start < end) {
            setAnimatedStats(prev => ({
              ...prev,
              [stat.id]: Math.floor(start)
            }));
            requestAnimationFrame(animate);
          } else {
            setAnimatedStats(prev => ({
              ...prev,
              [stat.id]: end
            }));
          }
        };

        animate();
      });
    }
  }, [isVisible]);

  const getColorClass = (color) => {
    const colorMap = {
      primary: 'bg-primary text-white',
      success: 'bg-success text-white',
      accent: 'bg-accent text-white',
      warning: 'bg-warning text-white',
      secondary: 'bg-secondary text-white'
    };
    return colorMap[color] || 'bg-primary text-white';
  };

  const getBorderClass = (color) => {
    const borderMap = {
      primary: 'border-primary/20',
      success: 'border-success/20',
      accent: 'border-accent/20',
      warning: 'border-warning/20',
      secondary: 'border-secondary/20'
    };
    return borderMap[color] || 'border-primary/20';
  };

  return (
    <section id="statistics-section">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-foreground mb-4">Our Impact by Numbers</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          These metrics reflect our commitment to delivering exceptional results and building lasting partnerships
        </p>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:grid lg:grid-cols-3 gap-8 mb-12">
        {statistics?.map((stat) => (
          <div 
            key={stat?.id}
            className={`bg-white rounded-2xl shadow-soft border-2 ${getBorderClass(stat?.color)} p-8 text-center hover:shadow-elevation transition-smooth group`}
          >
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${getColorClass(stat?.color)} flex items-center justify-center group-hover:scale-110 transition-smooth`}>
              <Icon name={stat?.icon} size={24} />
            </div>
            
            <div className="mb-2">
              <span className="text-4xl font-bold text-foreground">
                {animatedStats[stat?.id] || 0}{stat?.suffix}
              </span>
            </div>
            
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {stat?.label}
            </h3>
            
            <p className="text-sm text-muted-foreground">
              {stat?.description}
            </p>
          </div>
        ))}
      </div>

      {/* Tablet Layout */}
      <div className="hidden md:grid lg:hidden md:grid-cols-2 gap-6 mb-12">
        {statistics?.map((stat) => (
          <div 
            key={stat?.id}
            className={`bg-white rounded-xl shadow-soft border-2 ${getBorderClass(stat?.color)} p-6 text-center hover:shadow-elevation transition-smooth`}
          >
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className={`w-12 h-12 rounded-full ${getColorClass(stat?.color)} flex items-center justify-center`}>
                <Icon name={stat?.icon} size={20} />
              </div>
              <div className="text-left">
                <div className="text-3xl font-bold text-foreground">
                  {animatedStats[stat?.id] || 0}{stat?.suffix}
                </div>
                <div className="text-sm text-muted-foreground">{stat?.description}</div>
              </div>
            </div>
            <h3 className="font-semibold text-foreground">{stat?.label}</h3>
          </div>
        ))}
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden space-y-4 mb-12">
        {statistics?.map((stat) => (
          <div 
            key={stat?.id}
            className={`bg-white rounded-xl shadow-soft border-2 ${getBorderClass(stat?.color)} p-6`}
          >
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 rounded-full ${getColorClass(stat?.color)} flex items-center justify-center flex-shrink-0`}>
                <Icon name={stat?.icon} size={20} />
              </div>
              <div className="flex-1">
                <div className="flex items-baseline space-x-2 mb-1">
                  <span className="text-2xl font-bold text-foreground">
                    {animatedStats[stat?.id] || 0}{stat?.suffix}
                  </span>
                  <span className="text-sm font-medium text-foreground">{stat?.label}</span>
                </div>
                <p className="text-sm text-muted-foreground">{stat?.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Context */}
      <div className="bg-gradient-to-r from-muted/50 to-background rounded-2xl p-8 lg:p-12">
        <div className="grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-bold text-foreground mb-4">Behind the Numbers</h3>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              These statistics represent more than just numbers—they reflect real business transformations, 
              lasting partnerships, and the collective expertise of our talented team. Each project contributes 
              to our growing experience and deepens our understanding of how AI can drive meaningful change.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Our 98% client retention rate isn't just a metric; it's a testament to the relationships we build 
              and the ongoing value we provide to our partners in their AI journey.
            </p>
          </div>
          
          <div className="text-center lg:text-right">
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Icon name="Award" size={16} />
              <span>Certified Excellence</span>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div>ISO 27001 Certified</div>
              <div>SOC 2 Type II Compliant</div>
              <div>GDPR Compliant</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyStatistics;